import React from 'react';
import { Collapse, Form, Input, InputNumber, Select, Row, Col, Button, Popconfirm, Checkbox } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { Client, Trip, TravelCompanion, PreferredContactMethod, LoyaltyProgramEntry } from '../../types/client';
import { findClientByMobilePhone, findClientByEmail } from '../../services/clientsService';
import { getAllCities } from '../../services/listBuilderService';
import { clientCrmEn } from '../../content/clientCrm.en';
import styles from './ClientDetailsDrawer.module.css';

const { Panel } = Collapse;

interface ClientDetailsDrawerProps {
  client: Client;
  trip: Trip | null;
  onSave: (clientUpdates: Partial<Client>, tripUpdates: Partial<Trip>, options?: { showToast?: boolean }) => void;
  onDeleteClient?: () => void;
  onDuplicateFound?: (existingClientId: string) => void;
  userId?: string;
}

export const ClientDetailsDrawer: React.FC<ClientDetailsDrawerProps> = ({
  client,
  trip,
  onSave,
  onDeleteClient,
  onDuplicateFound,
  userId,
}) => {
  const [form] = Form.useForm();
  const hasContactInfo = !!(client.mobilePhone || client.workPhone || client.homePhone || client.personalEmail || client.workEmail);

  const handleMobilePhoneBlur = (): void => {
    if (!userId || !onDuplicateFound) return;
    const value = form.getFieldValue('mobilePhone') ?? '';
    if (!value || typeof value !== 'string') return;
    const existing = findClientByMobilePhone(userId, value, client.id);
    if (existing) onDuplicateFound(existing.id);
  };

  const handleEmailBlur = (): void => {
    if (!userId || !onDuplicateFound) return;
    const personal = form.getFieldValue('personalEmail') ?? '';
    const work = form.getFieldValue('workEmail') ?? '';
    const value = typeof personal === 'string' ? personal.trim() : '';
    const valueWork = typeof work === 'string' ? work.trim() : '';
    const toCheck = value || valueWork;
    if (!toCheck) return;
    const existing = findClientByEmail(userId, toCheck, client.id);
    if (existing) onDuplicateFound(existing.id);
  };
  const defaultExpanded = !client.firstName && !client.lastName
    ? ['contact', 'passport', 'preferences', 'companions']
    : hasContactInfo
      ? []
      : ['contact'];

  const handleFinish = (values: Record<string, unknown>): void => {
    const addr = (p: string) => ({
      line1: String(values[`${p}Line1`] ?? ''),
      line2: String(values[`${p}Line2`] ?? ''),
      city: String(values[`${p}City`] ?? ''),
      state: String(values[`${p}State`] ?? ''),
      zip: String(values[`${p}Zip`] ?? ''),
      country: String(values[`${p}Country`] ?? ''),
    });
    const clientUpdates: Partial<Client> = {
      firstName: String(values.firstName ?? ''),
      lastName: String(values.lastName ?? ''),
      mobilePhone: String(values.mobilePhone ?? ''),
      workPhone: String(values.workPhone ?? ''),
      homePhone: String(values.homePhone ?? ''),
      personalEmail: String(values.personalEmail ?? ''),
      workEmail: String(values.workEmail ?? ''),
      address: addr('address'),
      workAddress: addr('workAddress'),
      preferredContactMethod: (values.preferredContactMethod as PreferredContactMethod) ?? 'email',
      passport: values.passportNumber
        ? {
            number: String(values.passportNumber ?? ''),
            countryOfIssue: String(values.passportCountry ?? ''),
            dateOfIssue: (values.passportIssue as string) || null,
            dateOfExpiry: (values.passportExpiry as string) || null,
            placeOfBirth: String(values.passportPlaceOfBirth ?? ''),
          }
        : null,
      driversLicense: values.licenseNumber
        ? {
            number: String(values.licenseNumber ?? ''),
            state: String(values.licenseState ?? ''),
            expiration: (values.licenseExpiration as string) || null,
          }
        : null,
      loyaltyPrograms: client.loyaltyPrograms ?? [],
    };

    let tripUpdates: Partial<Trip> = {};
    if (trip) {
      const food = Array.isArray(values.food) ? values.food : [];
      const activities = Array.isArray(values.activityPreferences) ? values.activityPreferences : [];
      const accomm = Array.isArray(values.accommodationType) ? values.accommodationType : [];
      const cityIds = Array.isArray(values.cityIds) ? (values.cityIds as string[]).filter((x): x is string => typeof x === 'string') : undefined;
      tripUpdates = {
        cityIds: cityIds && cityIds.length > 0 ? cityIds : undefined,
        preferences: {
          ...trip.preferences,
          airClass: (values.airClass as Trip['preferences']['airClass']) ?? 'any',
          food: food as Trip['preferences']['food'],
          activityPreferences: activities as Trip['preferences']['activityPreferences'],
          accommodationType: accomm as Trip['preferences']['accommodationType'],
          accommodationBudgetPerNight: (values.accommodationBudget as number) ?? null,
          travelStyle: (values.travelStyle as Trip['preferences']['travelStyle']) ?? 'moderate',
        },
      };
    }
    onSave(clientUpdates, tripUpdates, { showToast: true });
  };

  const updateCompanion = (idx: number, updates: Partial<TravelCompanion>): void => {
    if (!trip) return;
    const companions = [...(trip.travelCompanions ?? [])];
    if (companions[idx]) {
      companions[idx] = { ...companions[idx], ...updates };
      onSave({}, { travelCompanions: companions }, { showToast: false });
    }
  };

  const addCompanion = (): void => {
    if (!trip) return;
    const companions = [...(trip.travelCompanions ?? [])];
    companions.push({
      id: crypto.randomUUID(),
      firstName: '',
      lastName: '',
      relationship: 'other',
      age: null,
      passport: null,
      driversLicense: null,
      loyaltyPrograms: [],
    });
    onSave({}, { travelCompanions: companions }, { showToast: false });
  };

  const removeCompanion = (idx: number): void => {
    if (!trip) return;
    const companions = (trip.travelCompanions ?? []).filter((_, i) => i !== idx);
    onSave({}, { travelCompanions: companions }, { showToast: false });
  };

  const loyaltyPrograms = (client.loyaltyPrograms ?? []) as LoyaltyProgramEntry[];

  const updateLoyaltyProgram = (idx: number, updates: Partial<LoyaltyProgramEntry>): void => {
    const updated = loyaltyPrograms.map((lp, i) => (i === idx ? { ...lp, ...updates } : lp));
    onSave({ loyaltyPrograms: updated }, {}, { showToast: false });
  };

  const addLoyaltyProgram = (): void => {
    const updated = [
      ...loyaltyPrograms,
      { id: crypto.randomUUID(), program: '', number: '', preferred: false },
    ];
    onSave({ loyaltyPrograms: updated }, {}, { showToast: false });
  };

  const removeLoyaltyProgram = (idx: number): void => {
    const updated = loyaltyPrograms.filter((_, i) => i !== idx);
    onSave({ loyaltyPrograms: updated }, {}, { showToast: false });
  };

  const tomorrow = ((): string => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  })();

  const yesterday = ((): string => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  })();

  const passportExpiryFromIssue = (issueDate: string): string => {
    const d = new Date(issueDate + 'T12:00:00Z');
    d.setFullYear(d.getFullYear() + 10);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  };

  React.useEffect(() => {
    form.setFieldsValue({
      firstName: client.firstName ?? '',
      lastName: client.lastName ?? '',
      mobilePhone: client.mobilePhone ?? '',
      workPhone: client.workPhone ?? '',
      homePhone: client.homePhone ?? '',
      personalEmail: client.personalEmail ?? '',
      workEmail: client.workEmail ?? '',
      preferredContactMethod: client.preferredContactMethod ?? 'email',
      addressLine1: client.address?.line1 ?? '',
      addressLine2: client.address?.line2 ?? '',
      addressCity: client.address?.city ?? '',
      addressState: client.address?.state ?? '',
      addressZip: client.address?.zip ?? '',
      addressCountry: client.address?.country ?? '',
      workAddressLine1: client.workAddress?.line1 ?? '',
      workAddressLine2: client.workAddress?.line2 ?? '',
      workAddressCity: client.workAddress?.city ?? '',
      workAddressState: client.workAddress?.state ?? '',
      workAddressZip: client.workAddress?.zip ?? '',
      workAddressCountry: client.workAddress?.country ?? '',
      passportNumber: client.passport?.number ?? '',
      passportCountry: client.passport?.countryOfIssue ?? '',
      passportIssue: client.passport?.dateOfIssue ?? undefined,
      passportExpiry: client.passport?.dateOfExpiry ?? undefined,
      passportPlaceOfBirth: client.passport?.placeOfBirth ?? '',
      licenseNumber: client.driversLicense?.number ?? '',
      licenseState: client.driversLicense?.state ?? '',
      licenseExpiration: client.driversLicense?.expiration ?? undefined,
      cityIds: trip?.cityIds ?? [],
      airClass: trip?.preferences?.airClass ?? 'any',
      food: trip?.preferences?.food ?? [],
      activityPreferences: trip?.preferences?.activityPreferences ?? [],
      accommodationType: trip?.preferences?.accommodationType ?? [],
      accommodationBudget: trip?.preferences?.accommodationBudgetPerNight ?? undefined,
      travelStyle: trip?.preferences?.travelStyle ?? 'moderate',
    });
  }, [client, trip, form]);

  const foodOptions = Object.entries(clientCrmEn.preferences.foodOptions).map(([k, v]) => ({ value: k, label: v }));
  const activityOptions = Object.entries(clientCrmEn.preferences.activityOptions).map(([k, v]) => ({ value: k, label: v }));
  const accommOptions = Object.entries(clientCrmEn.preferences.accommodationOptions).map(([k, v]) => ({ value: k, label: v }));

  return (
    <Collapse defaultActiveKey={defaultExpanded}>
      <Panel header={clientCrmEn.clientDetails} key="contact">
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Contact & addresses</div>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="firstName" label={clientCrmEn.name.firstName}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label={clientCrmEn.name.lastName}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="mobilePhone" label={clientCrmEn.contact.mobilePhone}>
                <Input onBlur={handleMobilePhoneBlur} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="workPhone" label={clientCrmEn.contact.workPhone}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="homePhone" label={clientCrmEn.contact.homePhone}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="personalEmail" label={clientCrmEn.contact.personalEmail}>
                <Input type="email" onBlur={handleEmailBlur} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="workEmail" label={clientCrmEn.contact.workEmail}>
                <Input type="email" onBlur={handleEmailBlur} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="preferredContactMethod" label={clientCrmEn.contact.preferredContactMethod}>
            <Select
              options={[
                { value: 'email', label: clientCrmEn.contact.preferredContact.email },
                { value: 'phone', label: clientCrmEn.contact.preferredContact.phone },
                { value: 'text', label: clientCrmEn.contact.preferredContact.text },
              ]}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{clientCrmEn.address.sectionTitle}</div>
              <div className={styles.addressLineGap}>
                <Form.Item name="addressLine1" noStyle><Input placeholder="Line 1" /></Form.Item>
              </div>
              <Form.Item name="addressLine2" noStyle><Input placeholder="Line 2" /></Form.Item>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="addressCity" label={clientCrmEn.address.city}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="addressState" label={clientCrmEn.address.state}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="addressZip" label={clientCrmEn.address.zip}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="addressCountry" label={clientCrmEn.address.country}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{clientCrmEn.workAddress.sectionTitle}</div>
              <div className={styles.addressLineGap}>
                <Form.Item name="workAddressLine1" noStyle><Input placeholder="Line 1" /></Form.Item>
              </div>
              <Form.Item name="workAddressLine2" noStyle><Input placeholder="Line 2" /></Form.Item>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="workAddressCity" label={clientCrmEn.address.city}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="workAddressState" label={clientCrmEn.address.state}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="workAddressZip" label={clientCrmEn.address.zip}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="workAddressCountry" label={clientCrmEn.address.country}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ fontWeight: 600, marginBottom: 8 }}>{clientCrmEn.passport.sectionTitle} & {clientCrmEn.driversLicense.sectionTitle}</div>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="passportNumber" label={clientCrmEn.passport.number}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="passportCountry" label={clientCrmEn.passport.countryOfIssue}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="passportIssue"
                label={clientCrmEn.passport.dateOfIssue}
                rules={[{
                  validator: (_, v) => {
                    if (!v) return Promise.resolve();
                    return new Date(v) < new Date() ? Promise.resolve() : Promise.reject(new Error('Issue date must be in the past'));
                  },
                }]}
              >
                <Input
                  type="date"
                  max={yesterday}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) form.setFieldValue('passportExpiry', passportExpiryFromIssue(v));
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="passportExpiry"
                label={clientCrmEn.passport.dateOfExpiry}
                rules={[{
                  validator: (_, v) => {
                    if (!v) return Promise.resolve();
                    return new Date(v) > new Date() ? Promise.resolve() : Promise.reject(new Error('Expiry must be in the future'));
                  },
                }]}
              >
                <Input type="date" min={tomorrow} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="passportPlaceOfBirth" label={clientCrmEn.passport.placeOfBirth}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="licenseNumber" label={clientCrmEn.driversLicense.number}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="licenseState" label={clientCrmEn.driversLicense.state}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="licenseExpiration"
                label={clientCrmEn.driversLicense.expiration}
                rules={[{
                  validator: (_, v) => {
                    if (!v) return Promise.resolve();
                    return new Date(v) > new Date() ? Promise.resolve() : Promise.reject(new Error('Expiration must be in the future'));
                  },
                }]}
              >
                <Input type="date" min={tomorrow} />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{clientCrmEn.loyaltyPrograms.sectionTitle}</div>
          {loyaltyPrograms.map((lp, idx) => (
            <div key={lp.id} style={{ marginBottom: 8 }}>
              <Row gutter={16} align="middle">
                <Col span={8}>
                  <Input
                    placeholder={clientCrmEn.loyaltyPrograms.programPlaceholder}
                    value={lp.program}
                    onChange={(e) => updateLoyaltyProgram(idx, { program: e.target.value })}
                  />
                </Col>
                <Col span={8}>
                  <Input
                    placeholder={clientCrmEn.loyaltyPrograms.numberPlaceholder}
                    value={lp.number}
                    onChange={(e) => updateLoyaltyProgram(idx, { number: e.target.value })}
                  />
                </Col>
                <Col span={4}>
                  <Checkbox
                    checked={lp.preferred}
                    onChange={(e) => updateLoyaltyProgram(idx, { preferred: e.target.checked })}
                  >
                    {clientCrmEn.loyaltyPrograms.preferred}
                  </Checkbox>
                </Col>
                <Col span={2}>
                  <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeLoyaltyProgram(idx)} />
                </Col>
              </Row>
            </div>
          ))}
          <Button
            type="dashed"
            onClick={addLoyaltyProgram}
            icon={<PlusOutlined />}
            block
            style={{ marginBottom: 16 }}
          >
            {clientCrmEn.loyaltyPrograms.addProgram}
          </Button>

          <div style={{ fontWeight: 600, marginBottom: 8 }}>{clientCrmEn.preferences.sectionTitle}</div>
          {trip && userId && (
            <Form.Item name="cityIds" label={clientCrmEn.trip.destinations}>
              <Select
                mode="multiple"
                placeholder={clientCrmEn.trip.destinationsPlaceholder}
                allowClear
                options={getAllCities(userId).map((c) => ({ value: c.id, label: `${c.name}, ${c.country}` }))}
              />
            </Form.Item>
          )}
          <Form.Item name="airClass" label={clientCrmEn.preferences.airClass}>
            <Select
              options={Object.entries(clientCrmEn.preferences.airClassOptions).map(([k, v]) => ({ value: k, label: v }))}
            />
          </Form.Item>
          <Form.Item name="food" label={clientCrmEn.preferences.food}>
            <Select mode="multiple" options={foodOptions} placeholder="Select" allowClear />
          </Form.Item>
          <Form.Item name="activityPreferences" label={clientCrmEn.preferences.activityPreferences}>
            <Select mode="multiple" options={activityOptions} placeholder="Select" allowClear />
          </Form.Item>
          <Form.Item name="accommodationType" label={clientCrmEn.preferences.accommodationType}>
            <Select mode="multiple" options={accommOptions} placeholder="Select" allowClear />
          </Form.Item>
          <Form.Item name="accommodationBudget" label={clientCrmEn.preferences.accommodationBudget}>
            <InputNumber min={0} style={{ width: '100%' }} prefix="$" />
          </Form.Item>
          <Form.Item name="travelStyle" label={clientCrmEn.preferences.travelStyle}>
            <Select
              options={Object.entries(clientCrmEn.preferences.travelStyleOptions).map(([k, v]) => ({ value: k, label: v }))}
            />
          </Form.Item>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Button type="primary" htmlType="submit">{clientCrmEn.actions.save}</Button>
            {onDeleteClient && (
              <Popconfirm
                title="Delete this client?"
                description="This will permanently delete the client and all associated notes, trips, and travel companions."
                onConfirm={onDeleteClient}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button danger>{clientCrmEn.actions.delete}</Button>
              </Popconfirm>
            )}
          </div>
        </Form>
      </Panel>
      <Panel header={clientCrmEn.travelCompanions.sectionTitle} key="companions">
        {(trip?.travelCompanions ?? []).map((comp, idx) => (
          <div key={comp.id} style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, marginBottom: 8 }}>
            <Row gutter={16} align="middle">
              <Col span={6}>
                <Input
                  placeholder={clientCrmEn.travelCompanions.firstName}
                  value={comp.firstName}
                  onChange={(e) => updateCompanion(idx, { firstName: e.target.value })}
                />
              </Col>
              <Col span={6}>
                <Input
                  placeholder={clientCrmEn.travelCompanions.lastName}
                  value={comp.lastName}
                  onChange={(e) => updateCompanion(idx, { lastName: e.target.value })}
                />
              </Col>
              <Col span={6}>
                <Select
                  style={{ width: '100%' }}
                  value={comp.relationship}
                  onChange={(v) => updateCompanion(idx, { relationship: v })}
                  options={Object.entries(clientCrmEn.travelCompanions.relationshipOptions).map(([k, v]) => ({ value: k, label: v }))}
                />
              </Col>
              <Col span={4}>
                <InputNumber
                  placeholder="Age"
                  value={comp.age ?? undefined}
                  onChange={(v) => updateCompanion(idx, { age: v ?? null })}
                  style={{ width: '100%' }}
                  min={0}
                />
              </Col>
              <Col span={2}>
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeCompanion(idx)} />
              </Col>
            </Row>
          </div>
        ))}
        <Button
          type="dashed"
          onClick={addCompanion}
          icon={<PlusOutlined />}
          block
          disabled={!trip}
        >
          {clientCrmEn.travelCompanions.addCompanion}
        </Button>
      </Panel>
    </Collapse>
  );
};
